import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoIcon } from "@/components/Icon";

/**
 * Gateway — first-time role fork (design v2).
 *
 * Two cards: I'm buying (slate-blue accent, opens existing ProProp app at
 * /workspace) and I'm renting (forest-green, opens the new chat-first /rent).
 * Agent users don't appear here — they enter directly via /pro/* and the
 * RequirePro guard sends them through /auth as needed.
 *
 * Press B for buy, R for rent. Bilingual via i18next; toggle in the topbar.
 */
export function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh") ?? false;
  const [hover, setHover] = useState<"buy" | "rent" | null>(null);

  const toggleLang = useCallback(() => {
    const next = isZh ? "en" : "zh";
    void i18n.changeLanguage(next);
  }, [i18n, isZh]);

  const pick = useCallback((track: "buy" | "rent") => {
    if (track === "buy") navigate("/buy");
    else navigate("/rent");
  }, [navigate]);

  // B / R keyboard shortcuts. Skip when the user is typing in a field —
  // unlikely on this page but cheap to guard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "b") pick("buy");
      else if (k === "r") pick("rent");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pick]);

  return (
    <div className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-bg text-text">
      {/* Topbar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-8">
        <div className="flex items-center gap-2.5">
          <LogoIcon size={28} />
          <span style={{ fontFamily: "var(--font-serif)" }} className="text-[20px] font-semibold tracking-[-0.02em]">
            ProProp
          </span>
          <span className="ml-1 rounded-full border border-border px-1.5 py-[1px] text-[10.5px] text-text-muted">
            SG
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Link
            to="/auth"
            className="rounded-md border border-transparent px-3 py-[5px] text-[12.5px] font-medium text-text-sec transition hover:border-border hover:text-text"
          >
            {t("landing.signIn", "Sign in")}
          </Link>
          <button
            onClick={toggleLang}
            title={t("common.switchLanguage", "Switch language")}
            className="rounded-md border border-border px-2.5 py-[4px] text-[11px] font-medium text-text-muted transition hover:border-border-strong hover:text-text"
          >
            <span className={isZh ? "opacity-50" : "opacity-100 font-bold"}>EN</span>
            <span className="mx-1.5 text-text-dim">·</span>
            <span className={isZh ? "opacity-100 font-bold" : "opacity-50"}>中</span>
          </button>
        </div>
      </header>

      {/* Data-source chip — only intro element above the cards. The
          serif hero copy was removed per design feedback (the cards
          themselves carry the framing). */}
      <div className="relative z-10 mx-auto mt-4 mb-6 flex max-w-[1080px] justify-center px-6 sm:mt-8 sm:mb-10 sm:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-[5px] text-[11.5px] text-text-sec">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--pos)" }} />
          {t("landing.dataChip", "HDB · URA · IRAS · OneMap — official data, May 2026")}
        </div>
      </div>

      {/* Two-card fork */}
      <div className="relative z-10 mx-auto grid w-full max-w-[1080px] gap-5 px-6 sm:px-8 sm:grid-cols-2">
        <RoleCard
          variant="buy"
          isZh={isZh}
          active={hover === "buy"}
          onMouseEnter={() => setHover("buy")}
          onMouseLeave={() => setHover(null)}
          onClick={() => pick("buy")}
        />
        <RoleCard
          variant="rent"
          isZh={isZh}
          active={hover === "rent"}
          onMouseEnter={() => setHover("rent")}
          onMouseLeave={() => setHover(null)}
          onClick={() => pick("rent")}
        />
      </div>

      {/* Bottom strip */}
      <div className="relative z-10 mx-auto mt-auto flex w-full max-w-[1100px] flex-wrap items-center justify-between gap-3 px-6 py-7 text-[11.5px] text-text-muted sm:px-8">
        <span>{t("landing.disclaimer", "Advisory only · not a CEA-licensed estate agency · no listings, no scraping")}</span>
        <span className="flex items-center gap-1.5">
          {isZh ? (
            <>按 <Kbd>B</Kbd> 买房 · <Kbd>R</Kbd> 租房</>
          ) : (
            <>Press <Kbd>B</Kbd> for Buy · <Kbd>R</Kbd> for Rent</>
          )}
        </span>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-border bg-surface px-1 text-[10.5px] font-semibold text-text-sec"
      style={{ fontFamily: "var(--font-mono)" }}
    >
      {children}
    </kbd>
  );
}

function RoleCard({
  variant, isZh, active, onClick, onMouseEnter, onMouseLeave,
}: {
  variant: "buy" | "rent";
  isZh: boolean;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const { t } = useTranslation();
  const isBuy = variant === "buy";
  const accentVar = isBuy ? "var(--buy-accent)" : "var(--rent-accent)";
  const accentDim = isBuy ? "var(--buy-accent-dim)" : "var(--rent-accent-dim)";
  const onAccent  = isBuy ? "var(--buy-accent-on)"  : "var(--rent-accent-on)";

  const featureKeys = isBuy
    ? ["buyFeat1", "buyFeat2", "buyFeat3", "buyFeat4", "buyFeat5", "buyFeat6"]
    : ["rentFeat1", "rentFeat2", "rentFeat3", "rentFeat4", "rentFeat5", "rentFeat6"];

  const titleKey = isBuy ? "buyTitle" : "rentTitle";
  const bodyKey  = isBuy ? "buyBody"  : "rentBody";
  const pillKey  = isBuy ? "buyPill"  : "rentPill";
  const tempoKey = isBuy ? "buyTempo" : "rentTempo";
  const ctaKey   = isBuy ? "buyCta"   : "rentCta";

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="relative overflow-hidden rounded-[18px] border bg-surface p-0 text-left transition-[transform,box-shadow] duration-200"
      style={{
        borderColor: active ? accentVar : "var(--rule)",
        boxShadow: active ? "0 18px 40px rgba(20,18,12,0.10), 0 2px 6px rgba(20,18,12,0.05)" : "0 1px 2px rgba(20,18,12,0.04)",
        transform: active ? "translateY(-2px)" : "none",
      }}
    >
      {/* Accent bar */}
      <div className="h-[4px] w-full" style={{ background: accentVar }} />

      <div className="px-7 pt-6 pb-5">
        {/* Header — pill + kbd */}
        <div className="mb-4 flex items-center justify-between">
          <span
            className="rounded-full px-2.5 py-[3px] text-[11px] font-semibold uppercase tracking-[0.04em]"
            style={{ background: accentDim, color: accentVar }}
          >
            {t(`landing.${pillKey}`, isBuy ? (isZh ? "我要买房" : "I'm buying") : (isZh ? "我要租房" : "I'm renting"))}
          </span>
          <Kbd>{isBuy ? "B" : "R"}</Kbd>
        </div>

        {/* Title */}
        <h2
          className="m-0 mb-2.5 font-medium tracking-[-0.015em]"
          style={{ fontFamily: "var(--font-serif)", fontSize: 28, lineHeight: 1.18 }}
        >
          {t(`landing.${titleKey}`,
            isBuy
              ? (isZh ? "为长期调研准备的工作台" : "Workspace for a long search")
              : (isZh ? "为一纸租约准备的快节奏对话" : "A quick chat for a short lease")
          )}
        </h2>
        <p className="m-0 text-[14px] leading-[1.55] text-text-sec">
          {t(`landing.${bodyKey}`,
            isBuy
              ? (isZh
                  ? "收藏房源、跑对比组、压测 ABSD 与 CPF、和伴侣共同规划。AI 只是其中一个面板。"
                  : "Save flats, run comp sets, stress-test ABSD and CPF, plan with a partner. The AI is one panel of many.")
              : (isZh
                  ? "只管问。D9 合理租金是多少？$3.5K 要交多少印花税？Work Permit 能租 HDB 吗？几秒出答案。"
                  : "Just ask. Fair rent for D9? Stamp duty on $3.5k? Can a Work Permit holder rent HDB? Answer in seconds.")
          )}
        </p>

        {/* Mini preview frame — uses --color-bg as the floor so the inner
            surface chips/cards float visibly in BOTH light and dark. (When
            the frame was surface-2, dark-mode surface cards inside it sat
            *below* the frame in the lightness scale and read as smudges.) */}
        <div
          className="relative mt-5 overflow-hidden rounded-[12px] border border-border-subtle"
          style={{ background: "var(--color-bg)", height: 170 }}
        >
          {isBuy
            ? <BuyPreview accentVar={accentVar} accentDim={accentDim} />
            : <RentPreview accentVar={accentVar} onAccent={onAccent} />}
        </div>

        {/* Feature grid */}
        <ul className="m-0 mt-5 grid list-none grid-cols-2 gap-x-3 gap-y-1.5 p-0">
          {featureKeys.map((k, i) => (
            <li key={k} className="flex items-center gap-2 text-[13px] text-text-sec">
              <span className="flex h-[14px] w-[14px] items-center justify-center" style={{ color: accentVar }}>
                <CheckIcon />
              </span>
              {t(`landing.${k}`, defaultFeatureLabel(variant, i, isZh))}
            </li>
          ))}
        </ul>

        {/* Footer — tempo hint + CTA chip */}
        <div className="mt-5 flex items-center justify-between">
          <span className="text-[12px] text-text-muted">
            {t(`landing.${tempoKey}`,
              isBuy
                ? (isZh ? "约 30 分钟使用 · 桌面与手机" : "~30 min sessions · desktop & mobile")
                : (isZh ? "约 3 分钟出答 · 手机优先" : "~3 min answers · mobile-first")
            )}
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-[7px] text-[13px] font-semibold"
            style={{ background: accentVar, color: onAccent }}
          >
            {t(`landing.${ctaKey}`,
              isBuy
                ? (isZh ? "进入买家工作台" : "Open buyer workspace")
                : (isZh ? "打开租房对话" : "Open rental chat")
            )}
            <ArrowIcon />
          </span>
        </div>
      </div>
    </button>
  );
}

function defaultFeatureLabel(variant: "buy" | "rent", i: number, isZh: boolean): string {
  const buy = isZh
    ? ["工作台看板", "已收藏房源与对比组", "ABSD / BSD / CPF 计算器", "夫妻共享资料", "长报告输出", "AI 对话带来源引用"]
    : ["Workspace dashboard", "Saved flats & comp sets", "ABSD / BSD / CPF tools", "Couple-shared profile", "Long-form reports", "AI chat with citations"];
  const rent = isZh
    ? ["对话为主的界面", "你所在区域的合理租金", "印花税计算", "资格检查", "合同条款问答", "可收藏回答"]
    : ["Chat-first surface", "Fair rent in your area", "Stamp duty calc", "Eligibility check", "Lease clauses Q&A", "Bookmark answers"];
  return (variant === "buy" ? buy : rent)[i] ?? "";
}

/* ── Mini previews — abstract little app frames inside each role card ─── */

function BuyPreview({ accentVar, accentDim }: { accentVar: string; accentDim: string }) {
  return (
    <div className="absolute inset-0 flex">
      {/* tiny sidebar */}
      <div className="flex w-[42px] flex-col items-center gap-2 border-r border-border-subtle bg-surface py-2.5">
        <div className="h-[16px] w-[16px] rounded-[3px]" style={{ background: accentVar }} />
        {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-[14px] w-[14px] rounded-[2px] bg-surface-3" />)}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-center justify-between">
          <div className="h-[8px] w-[100px] rounded-[2px] bg-text/85" />
          <div className="h-[16px] w-[50px] rounded-[3px] border" style={{ background: accentDim, borderColor: accentVar }} />
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-[5px] border border-border-subtle bg-surface px-1.5 py-1">
              <div className="h-[4px] w-[24px] rounded-[1px] bg-text-dim opacity-70" />
              <div
                className="mt-1 h-[10px] rounded-[2px]"
                style={{
                  width: i === 1 ? "44px" : "32px",
                  background: i === 1 ? accentVar : "var(--text-sec)",
                }}
              />
            </div>
          ))}
        </div>
        <div className="relative flex-1 overflow-hidden rounded-[5px] border border-border-subtle bg-surface p-1.5">
          <svg viewBox="0 0 120 50" preserveAspectRatio="none" className="h-full w-full">
            <path d="M2 40 L20 32 L38 36 L56 22 L74 26 L92 14 L118 10" fill="none" stroke={accentVar} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 40 L20 32 L38 36 L56 22 L74 26 L92 14 L118 10 L118 50 L2 50 Z" fill={accentDim} />
          </svg>
        </div>
      </div>
    </div>
  );
}

function RentPreview({ accentVar, onAccent }: { accentVar: string; onAccent: string }) {
  return (
    <div className="absolute inset-0 flex flex-col justify-end gap-2 px-5 py-4">
      {/* AI message */}
      <div className="flex max-w-[78%] items-start gap-2 self-start">
        <div
          className="flex h-[20px] w-[20px] items-center justify-center rounded-[5px] text-[9px] font-bold"
          style={{ background: accentVar, color: onAccent }}
        >
          AI
        </div>
        <div className="rounded-[10px] border border-border-subtle bg-surface px-2.5 py-2 text-[11.5px] leading-[1.45] text-text-sec">
          Median rent in D09 last 12mo is <strong className="text-text">S$5,200</strong>. Yours sits at the 35th percentile.
        </div>
      </div>
      {/* You message */}
      <div className="max-w-[70%] self-end">
        <div className="rounded-[10px] bg-surface-3 px-2.5 py-1.5 text-[11.5px] text-text">
          Is $4,800 fair for a 2BR in River Valley?
        </div>
      </div>
      {/* Composer */}
      <div className="mt-1 flex items-center gap-1.5 rounded-[12px] border border-border bg-surface px-2.5 py-2">
        <div className="h-[8px] flex-1 rounded-[2px] bg-surface-3 opacity-70" />
        <div className="h-[20px] w-[20px] rounded-[5px]" style={{ background: accentVar }} />
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 13 13" fill="none">
      <path d="M2.5 6.5l3 3 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <path d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
